import { isWeChatBrowser } from '../utils/isWeChatBrowser';
import { navigateToDownloadPage } from '../utils/navigateToDownloadPage';
import { initWechatJsSdk } from '../utils/wechatJsSdk';
import { WECHAT_OPEN_LAUNCH_APP_ID } from '../config/wechatOpen';

/**
 * 微信内置浏览器：拉取 JS-SDK 签名并 wx.config，供 `wx-open-launch-app` 使用。
 * 与 {@link circlePostInAppOpen} / {@link creatorContestActivityJoinApp} 混用时，App 内嵌 WebView 会跳过。
 */
export default {
  data() {
    return {
      /** @type {boolean} */
      wechatJsSdkReady: false,
      /** @type {boolean} */
      wechatJsSdkError: false,
    };
  },
  computed: {
    /**
     * 是否使用微信开放标签拉起 App（需 wx.config 已成功，用于依赖 SDK 就绪后的逻辑）
     * @returns {boolean}
     */
    useWechatOpenLaunchApp() {
      if (!WECHAT_OPEN_LAUNCH_APP_ID) return false;
      if (!isWeChatBrowser()) return false;
      if (this.isEmbeddedInQipaoApp()) return false;
      return this.wechatJsSdkReady;
    },
    /**
     * 是否在微信内渲染 `wx-open-launch-app` 按钮（不等待 config 完成，避免先展示普通按钮再切换导致闪烁）
     * @returns {boolean}
     */
    shouldUseWechatOpenLaunchTag() {
      if (!WECHAT_OPEN_LAUNCH_APP_ID) return false;
      if (!isWeChatBrowser()) return false;
      if (this.isEmbeddedInQipaoApp()) return false;
      return true;
    },
    /**
     * 开放平台移动应用 AppID（wx-open-launch-app 的 appid 属性）
     * @returns {string}
     */
    wechatOpenLaunchAppId() {
      return WECHAT_OPEN_LAUNCH_APP_ID;
    },
  },
  methods: {
    /**
     * App 内嵌 H5（原生注入桥）时不走微信开放标签
     * @returns {boolean}
     */
    isEmbeddedInQipaoApp() {
      if (typeof this.isInAppWebView === 'function' && this.isInAppWebView()) return true;
      if (typeof this.isActivityInAppWebView === 'function' && this.isActivityInAppWebView()) return true;
      return false;
    },
    /**
     * 在微信内初始化 JS-SDK（拉取服务端签名）。
     * 与 `VUE_APP_WECHAT_OPEN_LAUNCH_APP_ID` 无关：未配置该项时仍会调签名接口，
     * 仅影响是否渲染开放标签（开放平台移动应用 appid，见 `WECHAT_OPEN_LAUNCH_APP_ID`）。
     * @returns {Promise<void>}
     */
    async setupWechatJsSdk() {
      if (!isWeChatBrowser()) return;
      if (this.isEmbeddedInQipaoApp()) return;
      try {
        await initWechatJsSdk({ debug: false });
        this.wechatJsSdkReady = true;
      } catch (e) {
        this.wechatJsSdkError = true;
        if (typeof console !== 'undefined' && console.warn) {
          console.warn('[wechatJsSdk]', e);
        }
      }
    },
    /**
     * 微信开放标签唤起 App 失败时跳转下载页（与组件内默认行为一致，可供模板 @error 复用）
     * @returns {void}
     */
    onWechatOpenLaunchAppError() {
      navigateToDownloadPage(this.$router);
    },
  },
  mounted() {
    this.setupWechatJsSdk();
  },
};
